import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  ChevronDown, ChevronRight, X, Package, Building2, Layers, 
  TrendingUp, TrendingDown, Minus, Calendar, RefreshCw,
  ShoppingCart, IndianRupee, Users, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format currency
const formatCurrency = (value) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(2)}K`;
  return `₹${value.toFixed(2)}`;
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr.substring(0, 10);
  }
};

// Get current financial year dates
const getCurrentFYDates = () => {
  const today = new Date();
  const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    from: `${year}-04-01`,
    to: `${year + 1}-03-31`
  };
};

// Rate Trend Badge Component
const RateTrendBadge = ({ trend }) => {
  if (trend === 0 || trend === null || trend === undefined) {
    return (
      <Badge variant="outline" className="text-gray-500">
        <Minus className="w-3 h-3 mr-1" />
        0%
      </Badge>
    );
  }
  
  if (trend > 0) {
    return (
      <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
        <TrendingUp className="w-3 h-3 mr-1" />
        +{trend.toFixed(1)}%
      </Badge>
    );
  }
  
  return (
    <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
      <TrendingDown className="w-3 h-3 mr-1" />
      {trend.toFixed(1)}%
    </Badge>
  );
};

// Summary Card Component
const SummaryCard = ({ icon: Icon, label, value, subtext, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600"
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-xl font-bold">{value}</p>
            {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Item Row Component (Expandable)
const ItemRow = ({ item, isExpanded, onToggle }) => {
  return (
    <>
      <tr 
        className={`border-b hover:bg-muted/30 cursor-pointer ${isExpanded ? 'bg-muted/20' : ''}`}
        onClick={onToggle}
      >
        <td className="py-3 px-3">
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <div>
              <div className="font-medium text-sm">{item.item_name}</div>
              <div className="text-xs text-muted-foreground">{item.item_code}</div>
            </div>
          </div>
        </td>
        <td className="py-3 px-2 text-center">
          <Badge variant="outline" className="text-xs">{item.classification}</Badge>
        </td>
        <td className="py-3 px-2 text-right font-mono text-sm">{item.total_qty.toLocaleString()}</td>
        <td className="py-3 px-2 text-right font-mono text-sm font-medium">{formatCurrency(item.total_value)}</td>
        <td className="py-3 px-2 text-right font-mono text-sm">₹{item.avg_rate.toFixed(2)}</td>
        <td className="py-3 px-2 text-right font-mono text-sm">₹{item.last_rate.toFixed(2)}</td>
        <td className="py-3 px-2 text-center">
          <RateTrendBadge trend={item.rate_trend} />
        </td>
        <td className="py-3 px-2 text-center text-sm">{formatDate(item.last_purchase_date)}</td>
        <td className="py-3 px-2 text-center text-sm">{item.supplier_count}</td>
      </tr>
      
      {/* Expanded Transaction Details */}
      {isExpanded && item.transactions && item.transactions.length > 0 && (
        <tr>
          <td colSpan={9} className="bg-muted/10 p-0">
            <div className="p-3">
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                Transaction History ({item.transactions.length} purchases)
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-1.5 px-2">PO Number</th>
                    <th className="text-left py-1.5 px-2">GRN/PE</th>
                    <th className="text-left py-1.5 px-2">Date</th>
                    <th className="text-left py-1.5 px-2">Supplier</th>
                    <th className="text-right py-1.5 px-2">Qty</th>
                    <th className="text-right py-1.5 px-2">Rate</th>
                    <th className="text-right py-1.5 px-2">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {item.transactions.slice(0, 10).map((txn, idx) => (
                    <tr key={idx} className="border-b border-dashed last:border-0">
                      <td className="py-1.5 px-2 font-mono">{txn.po_number || '-'}</td>
                      <td className="py-1.5 px-2 font-mono">{txn.grn_number || txn.pe_number || '-'}</td>
                      <td className="py-1.5 px-2">{formatDate(txn.entry_date)}</td>
                      <td className="py-1.5 px-2">{txn.supplier_name}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{txn.quantity}</td>
                      <td className="py-1.5 px-2 text-right font-mono">₹{txn.rate?.toFixed(2)}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(txn.value)}</td>
                    </tr>
                  ))}
                  {item.transactions.length > 10 && (
                    <tr>
                      <td colSpan={7} className="py-2 text-center text-muted-foreground">
                        ... and {item.transactions.length - 10} more transactions
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const PurchaseDashboardContent = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  
  // Date range (default: current FY)
  const fyDates = getCurrentFYDates();
  const [dateFrom, setDateFrom] = useState(fyDates.from);
  const [dateTo, setDateTo] = useState(fyDates.to);
  
  // Cascading filters
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState(''); // Track selected supplier name
  const [classification, setClassification] = useState('');
  const [itemId, setItemId] = useState('');
  const [itemName, setItemName] = useState(''); // Track selected item name
  const [filterOrder, setFilterOrder] = useState([]); // Track order of filter selection
  
  // Filter options (dynamically updated)
  const [suppliers, setSuppliers] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [items, setItems] = useState([]);
  
  // Data
  const [summary, setSummary] = useState(null);
  const [itemSummaries, setItemSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  
  // Fetch filter options based on current selections
  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (supplierId) params.append('supplier_id', supplierId);
      if (classification) params.append('classification', classification);
      if (itemId) params.append('item_id', itemId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (filterOrder.length > 0) params.append('filter_order', filterOrder.join(','));
      
      const response = await axios.get(
        `${API_URL}/api/purchase-dashboard/filter-options?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      
      setSuppliers(response.data.suppliers || []);
      setClassifications(response.data.classifications || []);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, [supplierId, classification, itemId, dateFrom, dateTo, filterOrder, getAuthHeader]);
  
  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (supplierId) params.append('supplier_id', supplierId);
      if (classification) params.append('classification', classification);
      if (itemId) params.append('item_id', itemId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(
        `${API_URL}/api/purchase-dashboard/summary?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      
      setSummary(response.data.summary);
      setItemSummaries(response.data.items || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch purchase data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [supplierId, classification, itemId, dateFrom, dateTo, getAuthHeader, toast]);
  
  // Initial load and when filters change
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);
  
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  
  // Handle filter changes with order tracking
  const handleSupplierChange = (value) => {
    setSupplierId(value);
    // Find and store the supplier name
    const selectedSupplier = suppliers.find(s => s.id === value);
    setSupplierName(selectedSupplier?.name || value);
    if (value && !filterOrder.includes('supplier')) {
      setFilterOrder(prev => [...prev, 'supplier']);
    }
  };
  
  const handleClassificationChange = (value) => {
    setClassification(value);
    if (value && !filterOrder.includes('classification')) {
      setFilterOrder(prev => [...prev, 'classification']);
    }
  };
  
  const handleItemChange = (value) => {
    setItemId(value);
    // Find and store the item name
    const selectedItem = items.find(i => i.id === value);
    setItemName(selectedItem?.name || value);
    if (value && !filterOrder.includes('item')) {
      setFilterOrder(prev => [...prev, 'item']);
    }
  };
  
  // Clear individual filter
  const clearFilter = (filterType) => {
    if (filterType === 'supplier') {
      setSupplierId('');
      setSupplierName('');
    } else if (filterType === 'classification') {
      setClassification('');
    } else if (filterType === 'item') {
      setItemId('');
      setItemName('');
    }
    setFilterOrder(prev => prev.filter(f => f !== filterType));
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setSupplierId('');
    setSupplierName('');
    setClassification('');
    setItemId('');
    setItemName('');
    setFilterOrder([]);
  };
  
  // Reset to current FY
  const resetToCurrentFY = () => {
    const fy = getCurrentFYDates();
    setDateFrom(fy.from);
    setDateTo(fy.to);
  };
  
  return (
    <div className="p-6 space-y-6" data-testid="purchase-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="w-6 h-6" />
            Purchase Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            Analyze purchases by supplier, classification, and item
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSummary} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Date Range */}
            <div className="flex items-center gap-2">
              <div>
                <Label className="text-xs">From</Label>
                <Input 
                  type="date" 
                  value={dateFrom} 
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-36 h-9"
                />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input 
                  type="date" 
                  value={dateTo} 
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-36 h-9"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={resetToCurrentFY} className="h-9 mt-5">
                <Calendar className="w-4 h-4 mr-1" />
                FY
              </Button>
            </div>
            
            <div className="h-8 w-px bg-border" />
            
            {/* Supplier Filter */}
            <div className="min-w-[180px]">
              <Label className="text-xs flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                Supplier
                {filterOrder.indexOf('supplier') >= 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1">
                    {filterOrder.indexOf('supplier') + 1}
                  </Badge>
                )}
              </Label>
              <div className="flex items-center gap-1">
                {supplierId ? (
                  <div className="flex items-center gap-1 flex-1 h-9 px-3 border rounded-md bg-muted/30">
                    <span className="text-sm truncate flex-1">{supplierName}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1" onClick={() => clearFilter('supplier')}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Select value={supplierId} onValueChange={handleSupplierChange}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Suppliers" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            
            {/* Classification Filter */}
            <div className="min-w-[160px]">
              <Label className="text-xs flex items-center gap-1">
                <Layers className="w-3 h-3" />
                Classification
                {filterOrder.indexOf('classification') >= 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1">
                    {filterOrder.indexOf('classification') + 1}
                  </Badge>
                )}
              </Label>
              <div className="flex items-center gap-1">
                {classification ? (
                  <div className="flex items-center gap-1 flex-1 h-9 px-3 border rounded-md bg-muted/30">
                    <span className="text-sm truncate flex-1">{classification}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1" onClick={() => clearFilter('classification')}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Select value={classification} onValueChange={handleClassificationChange}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Classifications" />
                    </SelectTrigger>
                    <SelectContent>
                      {classifications.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            
            {/* Item Filter */}
            <div className="min-w-[200px]">
              <Label className="text-xs flex items-center gap-1">
                <Package className="w-3 h-3" />
                Item
                {filterOrder.indexOf('item') >= 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1">
                    {filterOrder.indexOf('item') + 1}
                  </Badge>
                )}
              </Label>
              <div className="flex items-center gap-1">
                {itemId ? (
                  <div className="flex items-center gap-1 flex-1 h-9 px-3 border rounded-md bg-muted/30">
                    <span className="text-sm truncate flex-1">{itemName}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1" onClick={() => clearFilter('item')}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <Select value={itemId} onValueChange={handleItemChange}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Items" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            
            {/* Clear All */}
            {(supplierId || classification || itemId) && (
              <Button variant="outline" size="sm" onClick={clearAllFilters} className="h-9 mt-5">
                Clear All
              </Button>
            )}
          </div>
          
          {/* Filter Order Indicator */}
          {filterOrder.length > 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <span>Filter Order:</span>
              {filterOrder.map((f, idx) => (
                <React.Fragment key={f}>
                  <Badge variant="outline" className="capitalize">{f}</Badge>
                  {idx < filterOrder.length - 1 && <span>→</span>}
                </React.Fragment>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <SummaryCard 
            icon={FileText} 
            label="Total POs" 
            value={summary.total_pos?.toLocaleString() || 0}
            color="blue"
          />
          <SummaryCard 
            icon={IndianRupee} 
            label="Total Value" 
            value={formatCurrency(summary.total_value || 0)}
            color="green"
          />
          <SummaryCard 
            icon={Package} 
            label="Unique Items" 
            value={summary.unique_items?.toLocaleString() || 0}
            color="purple"
          />
          <SummaryCard 
            icon={Users} 
            label="Unique Suppliers" 
            value={summary.unique_suppliers?.toLocaleString() || 0}
            color="orange"
          />
        </div>
      )}
      
      {/* Item Summary Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="w-4 h-4" />
            Item Summary
            <Badge variant="secondary" className="ml-2">{itemSummaries.length} items</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : itemSummaries.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No purchase data found for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                    <th className="text-left py-2.5 px-3 font-medium">Item Name</th>
                    <th className="text-center py-2.5 px-2 font-medium">Classification</th>
                    <th className="text-right py-2.5 px-2 font-medium">Total Qty</th>
                    <th className="text-right py-2.5 px-2 font-medium">Total Value</th>
                    <th className="text-right py-2.5 px-2 font-medium">Avg Rate</th>
                    <th className="text-right py-2.5 px-2 font-medium">Last Rate</th>
                    <th className="text-center py-2.5 px-2 font-medium">Rate Trend</th>
                    <th className="text-center py-2.5 px-2 font-medium">Last Purchase</th>
                    <th className="text-center py-2.5 px-2 font-medium">Suppliers</th>
                  </tr>
                </thead>
                <tbody>
                  {itemSummaries.map(item => (
                    <ItemRow 
                      key={item.item_id}
                      item={item}
                      isExpanded={expandedItem === item.item_id}
                      onToggle={() => setExpandedItem(expandedItem === item.item_id ? null : item.item_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Standalone page wrapper
const PurchaseDashboardPage = () => (
  <DashboardLayout>
    <PurchaseDashboardContent />
  </DashboardLayout>
);

export { PurchaseDashboardContent };
export default PurchaseDashboardPage;
